<template>
  <div class="remote-power">
    <div class="rp-card rp-header">
      <el-form :inline="true">
        <el-form-item :label="$str.getKey('参数设置/能量链')">
          <el-select v-model="eLink" @change="onELinkChange" style="min-width: 220px;">
            <el-option :value="item.uuid" :label="item.equipmentName" v-for="item in eLinks" :key="item.uuid">
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <el-button @click="openRecord" type="primary" plain>{{ $str.getKey('参数设置/命令记录') }}</el-button>
    </div>

    <div class="rp-desc">{{ $str.getKey('参数设置/eBlock启停') }}</div>
    <div class="rp-card" style="min-height: 140px;">
      <el-form inline label-width="120px" v-if="eBlockList.length > 0">
        <el-form-item :label="eBlock.equipmentName + ':'" v-for="eBlock in eBlockList" :key="eBlock.uuid">
          <EBlockStatus :status="eBlockStatus[eBlock.uuid]"></EBlockStatus>
          <el-button size="small" type="success" plain @click="enableEBlock(eBlock, 1)">{{ $str.getKey('参数设置/启用') }}</el-button>
          <el-button size="small" type="danger" plain @click="enableEBlock(eBlock, 0)">{{ $str.getKey('参数设置/停用') }}</el-button>
        </el-form-item>
      </el-form>
      <el-empty v-else :image-size="40"></el-empty>
    </div>

    <el-dialog v-model="isOpen" :title="$str.getKey('参数设置/命令记录')" width="900px">
      <div class="rp-card">
        <el-table :data="records" v-loading="loading" height="500">
          <el-table-column prop="devId" label="UUID"></el-table-column>
          <el-table-column :label="$str.getKey('参数设置/发送内容')">
            <template #default="{ row }">
              {{ $str.getKey('参数设置/发送时间') }}: {{ row.createTime }}
              <div v-if="row.commandContent.eblock_enable">
                <span>{{ $str.getKey('参数设置/eBlock启用') }}: </span>{{ row.commandContent.eblock_enable }}
              </div>
              <div v-if="row.commandContent.eblock_disable">
                <span>{{ $str.getKey('参数设置/eBlock停用') }}: </span>{{ row.commandContent.eblock_disable }}
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="$str.getKey('参数设置/接收内容')">
            <template #default="{ row }">
              <template v-if="(row.receiveCommandRecord && row.receiveCommandRecord.length > 0)">
                <div v-for="(rec, i) in row.receiveCommandRecord" :key="i">
                  {{ $str.getKey('参数设置/接收时间') }}: {{ rec.createTime }}
                  <div v-if="rec.commandContent.eblock_disable_list">
                    <span>{{ $str.getKey('参数设置/eBlock停用') }}:</span> {{ rec.commandContent.eblock_disable_list }}
                  </div>
                </div>
              </template>
              <div v-else>--</div>
            </template>
          </el-table-column>
          <el-table-column :label="$str.getKey('参数设置/操作者')">
            <template #default="{ row }">{{ row.createBy || '--' }}</template>
          </el-table-column>
        </el-table>

        <Pagination v-model="pageNum" :total="total" :page-size="pageSize" @change="updateRecords" />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { onUnmounted, ref, watch, watchEffect } from 'vue'
import { eui } from 'ems-ui'
import { ElMessageBox } from 'element-plus'
import useAppStore from '@/store/modules/app'
import { getElinkStruct } from '@/api/device/elink'
import { getTempatureRecord, commandSetting } from '@/api/command'
import { ws_req } from '@/ws/wss-request'
import EBlockStatus from './eblock-status.vue'
import Pagination from '@/components/Pagination/index.vue'

const $str = window.$str
const appStore = useAppStore()
const eLinks = ref([])
const eLink = ref('')

// ============ eLink 结构加载 ============
function watchStation() {
  watch(() => appStore.stationId, (id) => {
    if (id != null) {
      getElinkStruct(id).then(res => {
        eLinks.value = res.data || []
        selectFirstELink(eLinks.value)
      })
    }
  }, { immediate: true })
}
watchStation()

function selectFirstELink(list) {
  if (list.length > 0) {
    eLink.value = list[0].uuid
  }
}

function onELinkChange() {
  // 切换 eLink 后立即刷新状态
  updateFunc()
}

// ============ eBlock 列表与状态 ============
const eBlockList = ref([])
const eBlockStatus = ref({})

watchEffect(() => {
  if (eLink.value) {
    const cur = eLinks.value.find(elk => elk.uuid == eLink.value)
    if (cur) {
      eBlockList.value = cur.list || []
      eBlockStatus.value = {}
      ;(cur.list || []).forEach(it => {
        eBlockStatus.value[it.uuid] = 1
      })
      updateFunc()
    } else {
      eBlockList.value = []
      eBlockStatus.value = {}
    }
  }
})

// ============ WebSocket 轮询 eBlock 实时状态 ============
let eb_timer = null
async function updateFunc() {
  clearTimeout(eb_timer)
  try {
    const message = await ws_req.request('ws_eblock', { stationId: appStore.stationId })
    Object.keys(eBlockStatus.value).forEach(uuid => {
      if (Array.isArray(message)) {
        message.forEach(item => {
          if (Array.isArray(item.eblockList)) {
            item.eblockList.forEach(eb => {
              if (eb.uuid == uuid) {
                eBlockStatus.value[uuid] = eb.eblock_disable
              }
            })
          }
        })
      }
    })
  } catch (e) {
    console.warn('ws_eblock 获取失败', e)
  }

  eb_timer = setTimeout(() => {
    updateFunc()
  }, 1000 * 5)
}

onUnmounted(() => {
  clearTimeout(eb_timer)
})

// ============ eBlock 启用/停用 ============
function enableEBlock(eb, enable) {
  const curELink = eLinks.value.find(elk => elk.uuid == eLink.value)
  if (!curELink) return

  const key = enable ? 'eblock_enable' : 'eblock_disable'
  const label = enable ? $str.getKey('参数设置/启用') : $str.getKey('参数设置/停用')

  if (!eb.devId) {
    eui.showTopTip(`${eb.equipmentName} ${$str.getKey('参数设置/的dev_id不存在')}`, 'error')
    return
  }

  const params = {
    dev_id: curELink.devId,
    uuid: curELink.uuid,
    [key]: eb.devId,
    station_id: appStore.stationId + '',
  }

  ElMessageBox.confirm(`${$str.getKey('参数设置/确定')} ${label}?`, $str.getKey('参数设置/系统提示'), {
    confirmButtonText: $str.getKey('参数设置/确定'),
    cancelButtonText: $str.getKey('参数设置/取消'),
    type: 'warning'
  }).then(async () => {
    const r = await commandSetting(params)
    if (r.code == 200) {
      eui.showTopTip($str.getKey('参数设置/操作成功'), 'success')
      setTimeout(() => updateFunc(), 500)
    }
  }).catch(e => {
    console.warn(e)
  })
}

// ============ 命令记录 ============
const pageNum = ref(1)
const pageSize = ref(10)
const total = ref(0)
const records = ref([])
const loading = ref(false)
const isOpen = ref(false)

function openRecord() {
  isOpen.value = true
  pageNum.value = 1
  updateRecords()
}

function updateRecords() {
  loading.value = true
  getTempatureRecord({
    stationId: appStore.stationId,
    uuid: eLink.value,
    pageNum: pageNum.value,
    pageSize: pageSize.value,
  }).then(res => {
    parseContent(res.rows || [])
    records.value = res.rows || []
    total.value = res.total || 0
  }).finally(() => {
    loading.value = false
  })
}

function parseContent(list) {
  try {
    list.forEach(item => {
      item.commandContent = JSON.parse(item.commandContent)
      ;(item.receiveCommandRecord || []).forEach(rec => {
        rec.commandContent = JSON.parse(rec.commandContent)
      })
    })
  } catch (error) {
    console.warn(error)
  }
}
</script>

<style lang='scss' scoped>
.remote-power {
  padding: 4px 0;
}

.rp-card {
  background: rgba(255, 255, 255, 0.6);
  border-radius: 16px;
  padding: 16px 20px;
  margin-top: 12px;
}

.rp-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0;
}

.rp-desc {
  margin-top: 16px;
  margin-left: 4px;
  color: var(--color-black-45, rgba(0, 0, 0, 0.45));
}
</style>
